import type React from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "senf-shared";
import { useIsMobile } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";

interface NavigationProps {
  index: number;
  setIndex: React.Dispatch<React.SetStateAction<number>>;
  formik: any;
  isSubmitting: boolean;
  handleSubmit: (event?: any) => void;
  loading: boolean;
  editIdea: boolean;
  isOut: boolean;
  project: any;
  geoDataType: string;
  isGenerating: boolean;
  titleEditedManually: boolean;
}

const Navigation: React.FC<NavigationProps> = ({
  index,
  setIndex,
  formik,
  isSubmitting,
  handleSubmit,
  loading,
  editIdea,
  isOut,
  project,
  geoDataType,
  isGenerating,
  titleEditedManually,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const isMobile = useIsMobile();

  const isUserProfileComplete =
    user?.profileCompleted ||
    project?.settings?.demographicData?.collect === false;

  // Readable validation logic
  type Cond = { condition: boolean; notice: string };

  const evaluate = (conditions: Cond[]) => {
    const failing = conditions.find((c) => c.condition);
    return {
      disabled: !!failing,
      notice: failing ? failing.notice : t("dialogMap:add.unknownError"),
    };
  };

  const baseContinue = (): Cond[] => [
    {
      condition: !formik?.values?.address && geoDataType === "location",
      notice: t("dialogMap:add.locationRequired"),
    },
    {
      condition: isOut && geoDataType === "location",
      notice: t("dialogMap:add.outOfProjectArea"),
    },
  ];

  const formContinue = (): Cond[] => [
    // {
    //   condition: isGenerating,
    //   notice: t("dialogMap:add.postEnhancerRunning"),
    // },
    // {
    //   condition: !!formik?.errors?.body && titleEditedManually,
    //   notice: t("util:required", { val: t("global:title") }),
    // },
    // {
    //   condition: !!formik?.errors?.body,
    //   notice: t("util:required", { val: t("global:content") }),
    // },
    {
      condition: !!formik?.errors?.body || !formik?.values?.body?.trim(),
      notice: formik?.errors?.body || t("dialogMap:add.descriptionRequired"),
    },
    // {
    //   condition: !!formik?.errors?.topic,
    //   notice: formik?.errors?.topic || t("dialogMap:add.categoryRequired"),
    // },
    {
      condition:
        (geoDataType === "path" || geoDataType === "area") &&
        (formik.values.geoData?.features?.length === 0 ||
          !formik.values.geoData),
      notice: t("util:required", {
        val: geoDataType === "path" ? t("global:path") : t("global:area"),
      }),
    },
  ];

  const continueForStep = (step: number): Cond[] =>
    step === 0 ? baseContinue() : [...baseContinue(), ...formContinue()];

  const submitConditions = (): Cond[] => [
    // { condition: isSubmitting, notice: t("dialogMap:add.submitting") },
    ...baseContinue(),
    ...formContinue(),
  ];

  const { disabled: continueDisabled, notice: continueDisabledNotice } =
    evaluate(continueForStep(index));
  const { disabled: submitDisabled, notice: submitDisabledNotice } = evaluate(
    submitConditions(),
  );

  const showPrev = index > 0;
  const onPrev = () => setIndex((e) => Math.max(0, e - 1));
  const onNext: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (index === 0) return setIndex(1);
    if (index === 1 && !isUserProfileComplete) return setIndex(2);
    return handleSubmit(e);
  };

  const nextText =
    index === 0 || (index === 1 && !isUserProfileComplete)
      ? t("global:next")
      : editIdea
        ? t("dialogMap:add.updateIdea")
        : t("dialogMap:add.shareIdea");

  const nextDisabled = index === 0 ? continueDisabled : submitDisabled;
  const nextDisabledNotice =
    index === 0 ? continueDisabledNotice : submitDisabledNotice;

  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 w-full py-5",
        isMobile ? "fixed bottom-0 left-0 right-0 px-5" : "pt-0",
      )}
    >
      {showPrev && (
        <Button
          size="lg"
          leadingIcon="arrowhead-left-md"
          aria-label={t("global:previous")}
          onClick={onPrev}
        />
      )}
      <Button
        variant="primary"
        size="lg"
        aria-label={t("global:next")}
        text={nextText}
        classNameWrapper="w-full"
        width="max"
        onClick={onNext}
        loading={isSubmitting}
        disabled={nextDisabled}
        disabledNotice={nextDisabledNotice}
      />
    </div>
  );
};

export default Navigation;
